import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserPlus, Search, Building2, Shield, ChevronDown, ChevronRight,
  Check, X, Loader2, Download, Settings2, Eye, EyeOff, RefreshCw,
  AlertCircle, CheckCircle2, Info, Lock, Copy, Trash2, Power, Clock,
  Zap, Link2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';
import axios from 'axios';
import SamlSsoConfig from '@/components/tenant/SamlSsoConfig';
import DirectorySyncConfig from '@/components/tenant/DirectorySyncConfig';
import GoogleSigninConfig from '@/components/tenant/GoogleSigninConfig';
import {
  getTenantUsers,
  updateUserRole,
  updateUserMenuAccess,
  getUserMenuAccess,
  getAvailableMenus,
  getMockDirectoryUsers,
  importFromDirectory,
  importFromCSV,
  downloadCSVTemplate,
  updateUserAccessLevel,
  getAccessLevels,
  getScimConfigs,
  createScimConfig,
  toggleScimConfig,
  deleteScimConfig,
  getScimEvents,
  getScimGroups,
  getRoleMappings,
  createRoleMapping,
  deleteRoleMapping,
  TENANT_ROLES,
  DIRECTORY_PROVIDERS,
  ACCESS_LEVELS,
  getRoleColor,
  getRoleLabel,
  getAccessLevelColor,
  getAccessLevelLabel
} from '@/services/userManagement';

const UserManagement = () => {
  const { currentTenant, isSuperAdmin, userRole, hasPermission } = useTenant();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // All available roles (system + custom)
  const [allRoles, setAllRoles] = useState(TENANT_ROLES);
  
  // Available menus
  const [availableMenus, setAvailableMenus] = useState([]);
  
  // Directory import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [importDefaultRole, setImportDefaultRole] = useState('viewer');
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [importing, setImporting] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImportResult, setCsvImportResult] = useState(null);

  // SCIM Provisioning state
  const [scimConfigs, setScimConfigs] = useState([]);
  const [scimEvents, setScimEvents] = useState([]);
  const [scimLoading, setScimLoading] = useState(false);
  const [newTokenData, setNewTokenData] = useState(null);
  const [showScimSetup, setShowScimSetup] = useState(false);
  const [scimProvider, setScimProvider] = useState('google');
  const [scimGroups, setScimGroups] = useState([]);
  const [roleMappings, setRoleMappings] = useState([]);
  const [defaultMappings, setDefaultMappings] = useState([]);
  const [newMapping, setNewMapping] = useState({ group_name: '', role: 'viewer' });
  
  // Menu access config state
  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userMenuConfig, setUserMenuConfig] = useState([]);
  const [savingMenuAccess, setSavingMenuAccess] = useState(false);

  useEffect(() => {
    if (currentTenant?.id) {
      loadUsers();
      loadMenus();
      loadAllRoles();
    }
  }, [currentTenant?.id]);

  const loadAllRoles = async () => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/roles?tenant_id=${currentTenant?.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        const combined = [
          ...TENANT_ROLES,
          ...(data.custom_roles || []).map(r => ({
            value: r.id, label: r.name, description: r.description,
            color: 'bg-teal-100 text-teal-700'
          }))
        ];
        setAllRoles(combined);
      }
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const loadUsers = async () => {
    if (!currentTenant?.id) return;
    
    try {
      setLoading(true);
      const data = await getTenantUsers(currentTenant.id);
      setUsers(data.items || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadMenus = async () => {
    try {
      const data = await getAvailableMenus();
      setAvailableMenus(data.menus || []);
    } catch (err) {
      console.error('Failed to load menus:', err);
    }
  };

  // ========== Role Management ==========
  
  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(currentTenant.id, userId, newRole);
      toast.success('Role updated successfully');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  // ========== Access Level Management ==========
  
  const handleAccessLevelChange = async (userId, newAccessLevel) => {
    try {
      await updateUserAccessLevel(currentTenant.id, userId, newAccessLevel);
      toast.success('Access level updated successfully');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access level');
    }
  };

  // ========== Menu Access Management ==========
  
  const openMenuConfig = async (user) => {
    setSelectedUser(user);
    setShowMenuDialog(true);
    
    try {
      const config = await getUserMenuAccess(currentTenant.id, user.user_id);
      
      if (config.is_default || !config.menus?.length) {
        // Set all menus as enabled and visible by default
        const defaultConfig = availableMenus.map(menu => ({
          menu_id: menu.menu_id,
          label: menu.label,
          enabled: true,
          visible: true,
          submenus: (menu.submenus || []).map(sub => ({
            menu_id: sub.menu_id,
            label: sub.label,
            enabled: true,
            visible: true
          }))
        }));
        setUserMenuConfig(defaultConfig);
      } else {
        setUserMenuConfig(config.menus);
      }
    } catch (err) {
      console.error('Failed to load menu config:', err);
      // Set default config
      const defaultConfig = availableMenus.map(menu => ({
        menu_id: menu.menu_id,
        label: menu.label,
        enabled: true,
        visible: true,
        submenus: (menu.submenus || []).map(sub => ({
          menu_id: sub.menu_id,
          label: sub.label,
          enabled: true,
          visible: true
        }))
      }));
      setUserMenuConfig(defaultConfig);
    }
  };

  const toggleMenuVisibility = (menuId, visible) => {
    setUserMenuConfig(prev => prev.map(menu => {
      if (menu.menu_id === menuId) {
        return {
          ...menu,
          visible,
          enabled: visible ? menu.enabled : false,
          submenus: menu.submenus.map(sub => ({ ...sub, visible, enabled: visible ? sub.enabled : false }))
        };
      }
      return menu;
    }));
  };

  const toggleSubmenuVisibility = (menuId, submenuId, visible) => {
    setUserMenuConfig(prev => prev.map(menu => {
      if (menu.menu_id === menuId) {
        const updatedSubmenus = menu.submenus.map(sub => 
          sub.menu_id === submenuId ? { ...sub, visible, enabled: visible ? sub.enabled : false } : sub
        );
        // If all submenus are hidden, hide the parent
        const allHidden = updatedSubmenus.every(sub => !sub.visible);
        return {
          ...menu,
          visible: !allHidden,
          submenus: updatedSubmenus
        };
      }
      return menu;
    }));
  };

  const saveMenuAccess = async () => {
    if (!selectedUser) return;
    
    setSavingMenuAccess(true);
    try {
      await updateUserMenuAccess(currentTenant.id, selectedUser.user_id, userMenuConfig);
      toast.success('Menu access updated successfully');
      setShowMenuDialog(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save menu access');
    } finally {
      setSavingMenuAccess(false);
    }
  };

  // ========== Directory Import ==========
  
  const openImportDialog = (provider) => {
    setSelectedProvider(provider);
    setShowImportDialog(true);
    setDirectoryUsers([]);
    setSelectedUsers([]);
    setCsvFile(null);
    setCsvImportResult(null);
    if (provider.id !== 'csv') {
      loadDirectoryUsers(provider.id);
    }
  };

  const loadDirectoryUsers = async (providerId) => {
    setLoadingDirectory(true);
    try {
      const data = await getMockDirectoryUsers(currentTenant.id, providerId);
      setDirectoryUsers(data.users || []);
    } catch (err) {
      toast.error('Failed to fetch directory users');
    } finally {
      setLoadingDirectory(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const exists = prev.find(u => u.email === user.email);
      if (exists) {
        return prev.filter(u => u.email !== user.email);
      }
      return [...prev, user];
    });
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === directoryUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...directoryUsers]);
    }
  };

  const handleImport = async () => {
    if (selectedProvider?.id === 'csv') {
      return handleCSVImport();
    }
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user to import');
      return;
    }
    
    setImporting(true);
    try {
      const result = await importFromDirectory(
        currentTenant.id,
        selectedProvider.id,
        selectedUsers,
        importDefaultRole
      );
      
      toast.success(`Successfully imported ${result.summary.imported_count} users`);
      
      if (result.summary.skipped_count > 0) {
        toast.info(`${result.summary.skipped_count} users were skipped (already members)`);
      }
      
      setShowImportDialog(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import users');
    } finally {
      setImporting(false);
    }
  };

  const handleCSVImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setImporting(true);
    setCsvImportResult(null);
    try {
      const result = await importFromCSV(currentTenant.id, csvFile, importDefaultRole);
      setCsvImportResult(result);
      toast.success(`Imported ${result.summary.imported_count} of ${result.summary.total_rows} users`);
      if (result.summary.skipped_count > 0) {
        toast.info(`${result.summary.skipped_count} users skipped (already members)`);
      }
      if (result.summary.error_count > 0) {
        toast.warning(`${result.summary.error_count} rows had errors`);
      }
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV import failed');
    } finally {
      setImporting(false);
    }
  };

  // ========== SCIM Provisioning ==========

  const loadScimData = async () => {
    if (!currentTenant?.id) return;
    setScimLoading(true);
    try {
      const [configData, eventData, groupData, mappingData] = await Promise.all([
        getScimConfigs(currentTenant.id),
        getScimEvents(currentTenant.id, 30),
        getScimGroups(currentTenant.id),
        getRoleMappings(currentTenant.id)
      ]);
      setScimConfigs(configData.configs || []);
      setScimEvents(eventData.events || []);
      setScimGroups(groupData.groups || []);
      setRoleMappings(mappingData.mappings || []);
      setDefaultMappings(mappingData.defaults || []);
    } catch (err) {
      console.error('Failed to load SCIM data:', err);
    } finally {
      setScimLoading(false);
    }
  };

  const handleCreateScimToken = async () => {
    try {
      const result = await createScimConfig(currentTenant.id, scimProvider);
      setNewTokenData(result);
      toast.success(`SCIM token generated for ${scimProvider}`);
      loadScimData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate SCIM token');
    }
  };

  const handleToggleScim = async (configId, enabled) => {
    try {
      await toggleScimConfig(configId, enabled);
      toast.success(`SCIM provisioning ${enabled ? 'enabled' : 'disabled'}`);
      loadScimData();
    } catch (err) {
      toast.error('Failed to toggle SCIM config');
    }
  };

  const handleDeleteScim = async (configId) => {
    try {
      await deleteScimConfig(configId);
      toast.success('SCIM config deleted');
      loadScimData();
    } catch (err) {
      toast.error('Failed to delete SCIM config');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleAddMapping = async () => {
    if (!newMapping.group_name.trim()) { toast.error('Enter a group name'); return; }
    try {
      await createRoleMapping(currentTenant.id, newMapping.group_name, newMapping.role);
      toast.success(`Mapped "${newMapping.group_name}" → ${newMapping.role}`);
      setNewMapping({ group_name: '', role: 'viewer' });
      loadScimData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create mapping');
    }
  };

  const handleDeleteMapping = async (groupName) => {
    try {
      await deleteRoleMapping(groupName, currentTenant.id);
      toast.success('Mapping removed');
      loadScimData();
    } catch (err) {
      toast.error('Failed to delete mapping');
    }
  };

  // ========== Filtering ==========
  
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.department || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const canManageUsers = hasPermission('user:assign_role') || isSuperAdmin;
  const canCustomizePermissions = hasPermission('user:customize_permissions') || isSuperAdmin;
  const canInviteUsers = hasPermission('user:invite') || isSuperAdmin;

  if (!currentTenant) {
    return (
      <div className="text-center py-12" data-testid="no-org-message">
        <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Organization Selected</h3>
        <p className="text-gray-500">Please select an organization to manage users.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            User Management
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage users, roles, and menu access for {currentTenant.name}
          </p>
        </div>
        
        {canInviteUsers && (
          <div className="flex gap-2">
            {DIRECTORY_PROVIDERS.map(provider => (
              <Button
                key={provider.id}
                variant="outline"
                onClick={() => openImportDialog(provider)}
                className="flex items-center gap-2"
                data-testid={`import-${provider.id}-btn`}
              >
                <span>{provider.icon}</span>
                Import from {provider.name}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Role-Based Access Control</p>
          <p className="mt-1">
            Assign roles and configure access levels for each user. Use the <strong>Access Level</strong> dropdown 
            to set Read/Write, Read Only, or Auditor permissions. Click <strong>Configure</strong> to control 
            which menus are visible to each user.
          </p>
        </div>
      </div>

      {/* SCIM Provisioning Section */}
      {canInviteUsers && (
        <Card data-testid="scim-provisioning-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-600" />
                  SCIM Provisioning (Auto-Sync)
                </CardTitle>
                <CardDescription>
                  Auto-provision users from Azure AD, Okta, or any SCIM 2.0 bridge. Google Workspace requires a bridge (GCDS / Okta / Rippling) — see the on-screen notice after you pick a provider.
                </CardDescription>
              </div>
              {!showScimSetup && (
                <Button variant="outline" size="sm" onClick={() => { setShowScimSetup(true); loadScimData(); }} data-testid="configure-scim-btn">
                  <Settings2 className="w-4 h-4 mr-1" /> Configure
                </Button>
              )}
            </div>
          </CardHeader>
          {showScimSetup && (
            <CardContent className="space-y-4">
              {/* Generate New Token */}
              <div className="p-4 border rounded-lg bg-gray-50 space-y-3">
                <p className="text-sm font-medium">Generate SCIM Token</p>
                <div className="flex items-center gap-3">
                  <Select value={scimProvider} onValueChange={setScimProvider}>
                    <SelectTrigger className="w-[200px]" data-testid="scim-provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google">Google Workspace</SelectItem>
                      <SelectItem value="azure">Azure AD</SelectItem>
                      <SelectItem value="generic">Generic SCIM 2.0</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleCreateScimToken} size="sm" data-testid="generate-scim-token-btn">
                    <Zap className="w-4 h-4 mr-1" /> Generate Token
                  </Button>
                </div>
                
                {/* New Token Display (shown once after generation) */}
                {newTokenData && (
                  <div className="p-3 border border-purple-200 bg-purple-50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-medium text-purple-700">Save this token now — it won't be shown again</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-white px-3 py-2 rounded border font-mono break-all" data-testid="scim-token-display">
                        {newTokenData.token}
                      </code>
                      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(newTokenData.token)} data-testid="copy-scim-token-btn">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="text-xs text-gray-600">
                      <p className="font-medium mb-1">SCIM Endpoint:</p>
                      <code className="bg-white px-2 py-1 rounded border text-xs">
                        {window.location.origin}/api/scim/v2
                      </code>
                    </div>
                    {newTokenData.setup_instructions?.[newTokenData.provider] && (
                      <div className="text-xs text-gray-600 mt-2">
                        {newTokenData.setup_instructions[newTokenData.provider].notice && (
                          <div
                            className="mb-3 p-2 rounded border border-amber-300 bg-amber-50 text-amber-900 flex gap-2"
                            data-testid="scim-provider-notice"
                          >
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                            <p className="text-[11px] leading-relaxed">
                              {newTokenData.setup_instructions[newTokenData.provider].notice}
                            </p>
                          </div>
                        )}
                        <p className="font-medium mb-1">Setup Steps:</p>
                        <ol className="list-decimal list-inside space-y-1">
                          {newTokenData.setup_instructions[newTokenData.provider].steps.map((step, i) => (
                            <li key={`step-${i}`}>{step.replace(newTokenData.token, '(token above)')}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Active Configs */}
              {scimLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : scimConfigs.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Active Connections</p>
                  {scimConfigs.map(cfg => (
                    <div key={cfg.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{cfg.provider === 'google' ? '🔵' : cfg.provider === 'azure' ? '🔷' : '🔗'}</span>
                        <div>
                          <p className="text-sm font-medium capitalize">{cfg.provider === 'generic' ? 'Generic SCIM' : cfg.provider === 'google' ? 'Google Workspace' : 'Azure AD'}</p>
                          <p className="text-xs text-muted-foreground">
                            {cfg.request_count || 0} requests
                            {cfg.last_used_at ? ` · Last used ${new Date(cfg.last_used_at).toLocaleDateString()}` : ' · Never used'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={cfg.enabled ? 'default' : 'secondary'} className="text-xs">
                          {cfg.enabled ? 'Active' : 'Disabled'}
                        </Badge>
                        <Switch checked={cfg.enabled} onCheckedChange={(v) => handleToggleScim(cfg.id, v)} data-testid={`scim-toggle-${cfg.provider}`} />
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteScim(cfg.id)} className="text-red-500 hover:text-red-700" data-testid={`scim-delete-${cfg.provider}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">No SCIM connections configured yet</p>
              )}

              {/* Recent Events */}
              {scimEvents.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Recent Provisioning Events
                  </p>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {scimEvents.slice(0, 15).map(evt => (
                      <div key={evt.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{evt.action}</Badge>
                          <span className="text-muted-foreground">{evt.details?.email || evt.details?.display_name || evt.resource_id?.slice(0,8)}</span>
                        </div>
                        <span className="text-muted-foreground">{new Date(evt.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Group-to-Role Mapping */}
              <div className="space-y-3 border-t pt-4" data-testid="scim-role-mapping-section">
                <p className="text-sm font-medium flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5 text-indigo-600" /> Group-to-Role Mapping
                </p>
                <p className="text-xs text-muted-foreground">
                  When a user is added to a group via SCIM, their platform role is automatically set based on these mappings.
                </p>

                {/* Add New Mapping */}
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Group name (e.g. Security, Admins)"
                    value={newMapping.group_name}
                    onChange={e => setNewMapping(prev => ({ ...prev, group_name: e.target.value }))}
                    className="flex-1 h-9 text-sm"
                    data-testid="new-mapping-group-input"
                  />
                  <Select value={newMapping.role} onValueChange={v => setNewMapping(prev => ({ ...prev, role: v }))}>
                    <SelectTrigger className="w-[150px] h-9" data-testid="new-mapping-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {allRoles.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={handleAddMapping} data-testid="add-mapping-btn" className="h-9">
                    Add
                  </Button>
                </div>

                {/* Custom Mappings */}
                {roleMappings.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Custom Mappings</p>
                    {roleMappings.map(m => (
                      <div key={m.group_name} className="flex items-center justify-between text-sm p-2 bg-indigo-50 rounded border border-indigo-100">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">{m.group_name}</Badge>
                          <span className="text-gray-400">→</span>
                          <Badge className={getRoleColor(m.role)}>{getRoleLabel(m.role)}</Badge>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteMapping(m.group_name)} data-testid={`delete-mapping-${m.group_name}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Default Mappings */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Default Mappings (built-in)</p>
                  <div className="grid grid-cols-2 gap-1">
                    {defaultMappings.slice(0, 8).map(m => (
                      <div key={m.group_name} className="flex items-center gap-2 text-xs p-1.5 bg-gray-50 rounded">
                        <span className="font-mono text-gray-600">{m.group_name}</span>
                        <span className="text-gray-400">→</span>
                        <Badge className={`${getRoleColor(m.role)} text-[10px]`}>{getRoleLabel(m.role)}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Synced Groups */}
              {scimGroups.length > 0 && (
                <div className="space-y-2 border-t pt-4" data-testid="scim-groups-section">
                  <p className="text-sm font-medium flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" /> Synced Groups ({scimGroups.length})
                  </p>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {scimGroups.map(g => (
                      <div key={g.id} className="flex items-center justify-between text-sm p-2 border rounded">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{g.display_name}</span>
                          <Badge variant="outline" className="text-[10px]">{(g.members || []).length} members</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground capitalize">{g.provider}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* SAML SSO Section (Feb 2026) */}
      {canInviteUsers && (
        <GoogleSigninSection />
      )}

      {canInviteUsers && (
        <SamlSsoSection />
      )}

      {/* Directory Sync Section (Feb 2026) — pull users from Azure/Google/Okta */}
      {canInviteUsers && (
        <DirectorySyncSection />
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]" data-testid="role-filter">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {allRoles.map(role => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadUsers} data-testid="refresh-users-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members ({filteredUsers.length})</CardTitle>
          <CardDescription>
            View and manage user roles, access levels, and menu visibility
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12" data-testid="no-users-message">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || roleFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Import users from a directory service to get started'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">User</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Department</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Role</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Access Level</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Source</th>
                    {canCustomizePermissions && (
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Menu Visibility</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const isOwner = user.user_id === currentTenant.owner_id;
                    return (
                      <tr key={user.id} className="border-b hover:bg-gray-50" data-testid={`user-row-${user.email}`}>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-indigo-600 font-semibold">
                                {(user.full_name || user.email || '?')[0].toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{user.full_name || 'Unknown'}</p>
                                {isOwner && (
                                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                    Owner
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-sm">{user.department || '-'}</p>
                            <p className="text-xs text-gray-500">{user.job_title || ''}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {canManageUsers && !isOwner ? (
                            <Select
                              value={user.role}
                              onValueChange={(v) => handleRoleChange(user.user_id, v)}
                            >
                              <SelectTrigger className="w-[140px]" data-testid={`role-select-${user.email}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {allRoles.filter(r => 
                                  r.value !== 'admin' || isSuperAdmin
                                ).map(role => (
                                  <SelectItem key={role.value} value={role.value}>
                                    {role.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={getRoleColor(user.role)}>
                              {getRoleLabel(user.role)}
                            </Badge>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {canManageUsers && !isOwner ? (
                            <Select
                              value={user.access_level || 'read_write'}
                              onValueChange={(v) => handleAccessLevelChange(user.user_id, v)}
                            >
                              <SelectTrigger className="w-[140px]" data-testid={`access-level-select-${user.email}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ACCESS_LEVELS.map(level => (
                                  <SelectItem key={level.value} value={level.value}>
                                    {level.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={getAccessLevelColor(user.access_level || 'read_write')}>
                              {getAccessLevelLabel(user.access_level || 'read_write')}
                            </Badge>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {user.directory_source ? (
                            <Badge variant="outline" className="text-xs">
                              {user.directory_source === 'google' ? '🔵 Google' : 
                               user.directory_source === 'azure' ? '🔷 Azure' : 
                               user.directory_source === 'csv' ? '📄 CSV' : user.directory_source}
                            </Badge>
                          ) : (
                            <span className="text-sm text-gray-400">Manual</span>
                          )}
                        </td>
                        {canCustomizePermissions && (
                          <td className="py-4 px-4 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openMenuConfig(user)}
                              className="gap-1"
                              data-testid={`configure-menu-btn-${user.email}`}
                            >
                              <Eye className="w-4 h-4" />
                              Configure
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Directory Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{selectedProvider?.icon}</span>
              Import from {selectedProvider?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedProvider?.id === 'csv'
                ? 'Upload a CSV file with user data (email, full_name, department, job_title, role)'
                : 'Select users to import and assign them a default role'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Default Role Selection */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <Label className="whitespace-nowrap">Default Role:</Label>
              <Select value={importDefaultRole} onValueChange={setImportDefaultRole}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allRoles.filter(r => r.value !== 'admin' && r.value !== 'owner').map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500">
                Applied to users without a specified role
              </span>
            </div>

            {/* CSV Import Mode */}
            {selectedProvider?.id === 'csv' ? (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    id="csv-file-input"
                    className="hidden"
                    onChange={(e) => { setCsvFile(e.target.files[0]); setCsvImportResult(null); }}
                    data-testid="csv-file-input"
                  />
                  <label htmlFor="csv-file-input" className="cursor-pointer">
                    <Download className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="font-medium text-gray-700">
                      {csvFile ? csvFile.name : 'Click to select CSV file'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {csvFile ? `${(csvFile.size / 1024).toFixed(1)} KB` : 'Max 5MB'}
                    </p>
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadCSVTemplate()}
                    data-testid="download-csv-template-btn"
                  >
                    <Download className="w-3 h-3 mr-1" /> Download Template
                  </Button>
                  <span className="text-xs text-gray-500">
                    Required columns: <code className="bg-gray-100 px-1 rounded">email</code>. Optional: <code className="bg-gray-100 px-1 rounded">full_name, department, job_title, role</code>
                  </span>
                </div>

                {/* CSV Import Results */}
                {csvImportResult && (
                  <div className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-green-700">
                        <CheckCircle2 className="w-4 h-4" /> Imported: {csvImportResult.summary.imported_count}
                      </span>
                      {csvImportResult.summary.skipped_count > 0 && (
                        <span className="flex items-center gap-1 text-amber-700">
                          <AlertCircle className="w-4 h-4" /> Skipped: {csvImportResult.summary.skipped_count}
                        </span>
                      )}
                      {csvImportResult.summary.error_count > 0 && (
                        <span className="flex items-center gap-1 text-red-700">
                          <X className="w-4 h-4" /> Errors: {csvImportResult.summary.error_count}
                        </span>
                      )}
                    </div>
                    {csvImportResult.errors?.length > 0 && (
                      <div className="text-xs text-red-600 space-y-1 max-h-[120px] overflow-y-auto">
                        {csvImportResult.errors.map((e, i) => (
                          <p key={`err-${i}`}>Row {e.row}: {e.email} — {e.error}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Directory Import Mode */
              <>
                {loadingDirectory ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Select All */}
                    <div className="flex items-center justify-between border-b pb-3">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedUsers.length === directoryUsers.length && directoryUsers.length > 0}
                          onCheckedChange={selectAllUsers}
                        />
                        <span className="text-sm font-medium">
                          Select All ({directoryUsers.length} users)
                        </span>
                      </div>
                      <Badge variant="outline">
                        {selectedUsers.length} selected
                      </Badge>
                    </div>

                    {/* User List */}
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {directoryUsers.map(user => {
                        const isSelected = selectedUsers.some(u => u.email === user.email);
                        return (
                          <div
                            key={user.email}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              isSelected ? 'bg-indigo-50 border-indigo-200' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => toggleUserSelection(user)}
                          >
                            <Checkbox checked={isSelected} />
                            <div className="flex-1">
                              <p className="font-medium">{user.full_name}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm">{user.department}</p>
                              <p className="text-xs text-gray-500">{user.job_title}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={importing || (selectedProvider?.id === 'csv' ? !csvFile : selectedUsers.length === 0)}
              className="gap-2"
              data-testid="import-users-submit-btn"
            >
              {importing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {selectedProvider?.id === 'csv' 
                ? (csvFile ? `Import from ${csvFile.name}` : 'Select CSV File')
                : `Import ${selectedUsers.length} Users`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Visibility Configuration Dialog */}
      <Dialog open={showMenuDialog} onOpenChange={setShowMenuDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Configure Menu Visibility
            </DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <>
                  Control which menus <strong>{selectedUser.full_name || selectedUser.email}</strong> can see in the navigation
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-800">
                Toggle visibility to show or hide menus from this user's navigation. 
                Hidden menus will not appear in the sidebar. Module access is still 
                controlled by the tenant's license tier.
              </p>
            </div>

            <div className="space-y-2">
              {userMenuConfig.map((menu) => (
                <div key={menu.menu_id} className="border rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between p-3 bg-gray-50">
                    <div className="flex items-center gap-3">
                      <span className={`font-medium ${!menu.visible ? 'text-gray-400' : ''}`}>
                        {menu.label}
                      </span>
                      {!menu.visible && (
                        <Badge variant="outline" className="text-xs bg-gray-100 text-gray-500">
                          Hidden
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 mr-2">
                        {menu.visible ? 'Visible' : 'Hidden'}
                      </span>
                      <Switch
                        checked={menu.visible}
                        onCheckedChange={(checked) => toggleMenuVisibility(menu.menu_id, checked)}
                      />
                    </div>
                  </div>
                  
                  {menu.submenus?.length > 0 && menu.visible && (
                    <div className="p-3 space-y-2 border-t bg-white">
                      {menu.submenus.map(submenu => (
                        <div key={submenu.menu_id} className="flex items-center justify-between pl-4">
                          <span className={`text-sm ${!submenu.visible ? 'text-gray-400' : ''}`}>
                            {submenu.label}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">
                              {submenu.visible ? 'Visible' : 'Hidden'}
                            </span>
                            <Switch
                              checked={submenu.visible}
                              onCheckedChange={(checked) => 
                                toggleSubmenuVisibility(menu.menu_id, submenu.menu_id, checked)
                              }
                              disabled={!menu.visible}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMenuDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveMenuAccess} 
              disabled={savingMenuAccess}
              className="gap-2"
              data-testid="save-menu-access-btn"
            >
              {savingMenuAccess ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GoogleSigninSection = () => {
  const { currentTenant } = useTenant();
  const [expanded, setExpanded] = React.useState(false);
  if (!currentTenant?.id) return null;
  return (
    <Card data-testid="google-signin-section">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4 text-sky-600" />
              Sign in with Google
            </CardTitle>
            <CardDescription>
              Invited users can sign in with Google. Optionally let new people with your company email domain join automatically.
            </CardDescription>
          </div>
          <Button variant={expanded ? 'ghost' : 'outline'} size="sm" onClick={() => setExpanded(!expanded)} data-testid="configure-google-signin-btn">
            {expanded ? 'Collapse' : <><Settings2 className="w-4 h-4 mr-1" /> Configure</>}
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          <GoogleSigninConfig tenantId={currentTenant.id} />
        </CardContent>
      )}
    </Card>
  );
};

/**
 * SamlSsoSection — renders the full owner-level SAML SSO configuration
 * (SP metadata, XML upload, attribute mapping, JIT). Delegates to the
 * dedicated ``SamlSsoConfig`` component which owns all the state.
 */
const SamlSsoSection = () => {
  const { currentTenant } = useTenant();
  const [expanded, setExpanded] = React.useState(false);
  if (!currentTenant?.id) return null;
  return (
    <Card data-testid="saml-sso-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4 text-emerald-600" />
              SAML SSO (Custom IdP)
            </CardTitle>
            <CardDescription>
              Sign users in via Google Workspace, Azure AD, Okta, or any SAML 2.0 IdP.
              Upload IdP metadata XML, map attributes, and turn on Just-In-Time provisioning.
            </CardDescription>
          </div>
          {!expanded ? (
            <Button variant="outline" size="sm" onClick={() => setExpanded(true)} data-testid="configure-saml-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Configure
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setExpanded(false)} data-testid="collapse-saml-btn">
              Collapse
            </Button>
          )}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          <SamlSsoConfig
            tenantId={currentTenant.id}
            tenantName={currentTenant.name || 'this tenant'}
          />
        </CardContent>
      )}
    </Card>
  );
};

/**
 * DirectorySyncSection — sibling of ``SamlSsoSection``. Owner-level pull-based
 * directory sync (Azure AD / Google Workspace / Okta). Delegates to the
 * dedicated ``DirectorySyncConfig`` component which owns all the state.
 */
const DirectorySyncSection = () => {
  const { currentTenant } = useTenant();
  const [expanded, setExpanded] = React.useState(false);
  if (!currentTenant?.id) return null;
  return (
    <Card data-testid="directory-sync-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-600" />
              Directory Sync
            </CardTitle>
            <CardDescription>
              Auto-provision users from Microsoft Entra ID / Azure AD, Google Workspace, or Okta.
              Runs nightly on the scheduler with a manual "Sync now" button — new users get the
              default role you pick and deprovisioned users are deactivated locally.
            </CardDescription>
          </div>
          {!expanded ? (
            <Button variant="outline" size="sm" onClick={() => setExpanded(true)} data-testid="configure-directory-sync-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Configure
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setExpanded(false)} data-testid="collapse-directory-sync-btn">
              Collapse
            </Button>
          )}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          <DirectorySyncConfig tenantId={currentTenant.id} />
        </CardContent>
      )}
    </Card>
  );
};

export default UserManagement;
